import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Download } from 'lucide-react';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await adminFetch('/admin/orders');
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalRevenue = orders.reduce((sum, order) => sum + order.price, 0);

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-4xl font-bold font-display text-foreground">Orders</h1>
            <p className="text-muted-foreground mt-1">
              {orders.length} total orders • ${totalRevenue.toFixed(2)} revenue
            </p>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Item</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Type</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-foreground">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(order.purchased_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-foreground">{order.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-foreground font-semibold">
                        {order.item_title || 'Unknown'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {order.item_artist || 'Unknown Artist'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full">
                        {order.item_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-bold text-primary">${order.price.toFixed(2)}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {orders.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No orders yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};